import React, { useEffect } from "react";
import { trackPhoneConversion } from "./analytics";
import {
  CONTACT_ADDRESS,
  CONTACT_EMAIL,
  MANAGEMENT_COMPANY,
  OFFICE_PHONE_DISPLAY,
  OFFICE_PHONE_TEL,
  OFFICIAL_CONTACTS,
  REAL_ESTATE_SELLER,
  VACATION_CLUB_BRAND,
  WHATSAPP_DISPLAY,
  WHATSAPP_NUMBER,
} from "./contactConfig";

const luxuryGradient = "bg-gradient-to-br from-[#0B1C2C] via-[#0F2A44] to-[#123B63]";

const pages = {
  terms: {
    title: "Terms & Conditions",
    updated: "Last Updated: June 2026",
    intro: "Welcome to Sunny Vacation Club. By accessing and using this website, you agree to comply with and be bound by the following Terms and Conditions.",
    sections: [
      ["1. Website Purpose", "Sunny Vacation Club provides information regarding vacation ownership opportunities, membership programs, holiday exchange services, resort accommodations, and related travel services."],
      ["2. Information Accuracy", "We strive to ensure that all information displayed on this website is accurate and up to date. However, prices, availability, benefits, resort facilities, exchange opportunities, and promotional offers may change without prior notice."],
      ["3. Membership and Ownership", "Memberships, vacation ownership products, and exchange benefits are governed by separate contractual agreements signed between the customer and Sunny Vacation Club or its affiliated partners."],
      ["4. Reservations", "All reservations are subject to availability at the time of confirmation. Submission of a reservation request does not guarantee availability."],
      ["5. Intellectual Property", "All content on this website, including logos, images, text, graphics, and designs, is the property of Sunny Vacation Club unless otherwise stated."],
      ["6. Limitation of Liability", "Sunny Vacation Club shall not be liable for any direct, indirect, incidental, or consequential damages arising from the use of this website."],
      ["7. Third-Party Services", "This website may contain links to third-party websites and exchange networks. Sunny Vacation Club is not responsible for the content or policies of external websites."],
      ["8. Governing Law", "Any disputes arising from the use of this website shall be governed by the laws of Tunisia. The competent court for any dispute shall be the Court of First Instance of Tunis, Tunisia."],
    ],
  },
  privacy: {
    title: "Privacy Policy",
    updated: "Last Updated: June 2026",
    intro: "Sunny Vacation Club respects your privacy and is committed to protecting your personal information.",
    sections: [
      ["Information We Collect", "We may collect your name, email address, telephone number, country of residence, booking information, and membership inquiries."],
      ["How We Use Information", "Your information may be used to respond to inquiries, process reservations, provide membership information, improve customer service, and send promotional communications."],
      ["Data Protection", "We implement reasonable security measures to protect your personal information from unauthorized access, disclosure, or misuse."],
      ["Sharing Information", "We do not sell personal information to third parties. Information may be shared with resort partners, reservation providers, exchange program partners, and service providers assisting with operations."],
      ["Cookies", "This website may use cookies to improve user experience and website functionality."],
      ["Contact", `For privacy-related inquiries, contact ${CONTACT_EMAIL}.`],
    ],
  },
  cancellation: {
    title: "Cancellation & Refund Policy",
    updated: "Last Updated: June 2026",
    intro: "Cancellation and refund conditions may vary depending on the product, reservation type, resort, destination, and contractual terms provided during the purchase or booking process.",
    sections: [
      ["Membership Products", "Membership fees, ownership purchases, and associated administrative fees may be subject to specific contractual terms provided at the time of purchase."],
      ["Reservation Cancellations", "Cancellation policies may vary depending on resort, destination, reservation type, and promotional offer. Specific cancellation conditions will be communicated before confirmation."],
      ["Refunds", "Approved refunds, when applicable, will be processed using the original payment method. Processing times may vary depending on the payment provider and financial institution."],
      ["Force Majeure", "Sunny Vacation Club shall not be liable for cancellations, delays, or interruptions caused by circumstances beyond its reasonable control, including natural disasters, government restrictions, transportation disruptions, or other unforeseen events."],
    ],
  },
};

function ImportantNotice() {
  return (
    <section className="rounded-[1.5rem] border border-[#E5C07B]/40 bg-[#FFF8E8] p-6 text-[#0B1C2C] shadow-sm">
      <p className="text-sm font-black uppercase tracking-[0.25em] text-[#8A6A25]">Important Notice</p>
      <p className="mt-3 leading-8">
        Sunny Vacation Club presents vacation ownership, reservation, and travel-related opportunities connected to
        selected resort properties. Ownership rights, membership benefits, exchange options, pricing, availability, and
        travel offers are subject to verification, eligibility, and signed contractual agreements. Website information is
        provided for general presentation purposes only and does not constitute a binding sale offer.
      </p>
      <div className="mt-4 grid gap-2 text-sm font-bold sm:grid-cols-3">
        {OFFICIAL_CONTACTS.map((contact) => (
          <a key={contact.label} className="rounded-2xl border border-[#E5C07B]/30 bg-white/60 px-4 py-3" href={`tel:${contact.tel}`} onClick={() => trackPhoneConversion(`legal_notice_${contact.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_phone`, contact.tel)}>
            {contact.label}: {contact.display}
          </a>
        ))}
      </div>
    </section>
  );
}

export default function LegalPage({ type = "terms" }) {
  const page = pages[type] || pages.terms;

  useEffect(() => {
    document.documentElement.lang = "en";
    document.documentElement.dir = "ltr";
    document.title = `${page.title} | Sunny Vacation Club`;
  }, [page.title]);

  return (
    <main className="min-h-screen bg-[#FCFBF8] font-sans text-[#0B1C2C]">
      <header className={`${luxuryGradient} px-5 py-6 text-white`}>
        <div className="mx-auto flex max-w-6xl flex-col gap-5 sm:flex-row sm:items-center sm:justify-between">
          <a href="/" className="flex items-center gap-3">
            <img src="/brand/sunny-logo-header.png" alt="Sunny Vacation Club" className="h-12 w-12 rounded-full object-contain" />
            <span className="font-black">Sunny Vacation Club</span>
          </a>
          <nav className="flex flex-wrap gap-3 text-sm font-bold text-slate-200">
            <a className="hover:text-[#E5C07B]" href="/terms">Terms</a>
            <a className="hover:text-[#E5C07B]" href="/privacy">Privacy</a>
            <a className="hover:text-[#E5C07B]" href="/cancellation-refund">Cancellation</a>
            <a className="hover:text-[#E5C07B]" href="/#contact">Contact</a>
          </nav>
        </div>
      </header>

      <section className="px-5 py-14">
        <div className="mx-auto max-w-4xl">
          <p className="text-sm font-black uppercase tracking-[0.3em] text-[#8A6A25]">Legal</p>
          <h1 className="mt-3 text-4xl font-black md:text-5xl">{page.title}</h1>
          <p className="mt-3 text-sm font-bold text-[#4B5563]">{page.updated}</p>
          <p className="mt-8 text-lg leading-8 text-[#4B5563]">{page.intro}</p>

          <div className="mt-10">
            <ImportantNotice />
          </div>

          <div className="mt-10 space-y-8">
            {page.sections.map(([heading, body]) => (
              <section key={heading} className="rounded-[1.5rem] bg-white p-6 shadow-sm ring-1 ring-[#E5E7EB]">
                <h2 className="text-xl font-black">{heading}</h2>
                <p className="mt-3 leading-8 text-[#4B5563]">{body}</p>
              </section>
            ))}
          </div>
        </div>
      </section>

      <footer className={`${luxuryGradient} px-5 py-10 text-sm text-slate-200`}>
        <div className="mx-auto grid max-w-6xl gap-6 md:grid-cols-3">
          <div>
            <p className="font-black text-white">Company Legal Identity</p>
            <p className="mt-3 leading-7">Brand: {VACATION_CLUB_BRAND}</p>
            <p className="mt-2 leading-7">Real estate seller: {REAL_ESTATE_SELLER}</p>
            <p className="mt-2 leading-7">Management company: {MANAGEMENT_COMPANY}</p>
            <p className="mt-2 leading-7">Registration number: provided in purchase documentation.</p>
            <p className="mt-2 leading-7">Vacation ownership and travel-related marketing and reservation platform.</p>
          </div>
          <div>
            <p className="font-black text-white">Contact</p>
            <p className="mt-3">Email: <a className="text-[#E5C07B]" href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a></p>
            <p className="mt-2">WhatsApp: <a className="text-[#E5C07B]" href={`https://wa.me/${WHATSAPP_NUMBER}`}>{WHATSAPP_DISPLAY}</a></p>
            <p className="mt-2">Phone: <a className="text-[#E5C07B]" href={`tel:${OFFICE_PHONE_TEL}`} onClick={() => trackPhoneConversion("legal_office_phone", OFFICE_PHONE_TEL)}>{OFFICE_PHONE_DISPLAY}</a></p>
            {OFFICIAL_CONTACTS.map((contact) => (
              <p key={contact.label} className="mt-2">{contact.label}: <a className="text-[#E5C07B]" href={`tel:${contact.tel}`} onClick={() => trackPhoneConversion(`legal_footer_${contact.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")}_phone`, contact.tel)}>{contact.display}</a></p>
            ))}
          </div>
          <div>
            <p className="font-black text-white">Tunisia Office</p>
            <p className="mt-3 leading-7">{CONTACT_ADDRESS}</p>
          </div>
        </div>
      </footer>
    </main>
  );
}
